/**
 * VaultDropZone — drag & drop file upload area.
 * Uses the vault design system with the upload zone color accent.
 */

import React, { useState, useRef, useCallback } from "react";

interface VaultDropZoneProps {
  onFiles: (files: File[]) => void;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  compact?: boolean;
}

export const VaultDropZone: React.FC<VaultDropZoneProps> = ({
  onFiles,
  accept,
  multiple = true,
  disabled = false,
  compact = false,
}) => {
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragIn = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items?.length > 0) setDragOver(true);
  }, []);

  const handleDragOut = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (disabled) return;
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) onFiles(multiple ? files : [files[0]]);
  }, [disabled, multiple, onFiles]);

  const handleClick = () => {
    if (!disabled) inputRef.current?.click();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) onFiles(multiple ? files : [files[0]]);
    e.target.value = "";
  };

  return (
    <div
      className={`vault-zone-upload ${dragOver ? "vault-dropzone-active" : ""}`}
      onClick={handleClick}
      onDragEnter={handleDragIn}
      onDragLeave={handleDragOut}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      style={{
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        textAlign: "center",
        transition: "all 0.2s",
        padding: compact ? "var(--vault-spacing-sm)" : "var(--vault-spacing-lg) var(--vault-spacing-md)",
        border: dragOver ? "2px dashed var(--vault-zone-upload-border)" : "2px dashed var(--vault-grid-line)",
        borderLeft: dragOver ? "3px solid var(--vault-zone-upload-border)" : "3px solid var(--vault-zone-upload-border)",
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleChange}
        style={{ display: "none" }}
      />
      {compact ? (
        <span className="vault-label-tiny">
          {dragOver ? "Rilascia qui" : "Trascina file o clicca per caricare"}
        </span>
      ) : (
        <>
          <div style={{ fontSize: "2rem", color: "var(--vault-zone-upload-border)", marginBottom: "var(--vault-spacing-xs)" }}>
            ↑
          </div>
          <span className="vault-label-tiny" style={{ display: "block" }}>
            {dragOver ? "Rilascia i file qui" : "Trascina i file qui oppure clicca per selezionare"}
          </span>
          <span className="vault-text-muted" style={{ display: "block", marginTop: 4, fontSize: "var(--vault-text-tiny)" }}>
            PDF, immagini, documenti — max 10 MB per file
          </span>
        </>
      )}
    </div>
  );
};
